import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { NgxSpinnerService } from "ngx-spinner";
import { ToastrService } from "ngx-toastr";
import { Observable, throwError } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { ApiConfig } from "./api.config.service";
import { BadRequestError } from "./model/badrequest.error";
import { HttpMethod } from "./model/http-method.enum";
import { NotFoundError } from "./model/notfound.error";
import { UnAuthorizedError } from "./model/unauthorized.error";
import { UnknownError } from "./model/unknown.error";
import { ApiProxyService } from "./api.proxy.service";
/**
 * Generic HTTP data service to be used by all other services
 */
@Injectable({
  providedIn: "root",
})
export class ApiBaseService {
  private apiProxyService = inject(ApiProxyService);

  private spinnerAwait = false;
  private spinnerRunning = false;
  private openRequests = 0;
  protected urlBase: String = "";

  constructor(
    private http: HttpClient,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private trans: TranslateService,
    protected config: ApiConfig
  ) {}

  public request<T>(
    method: HttpMethod,
    endpoint: string,
    data?: any,
    headers?: { [key: string]: string },
    silent = false,
    urlOverride: boolean = false,
    url?: string
  ): Observable<T> {
    const token = localStorage.getItem("authToken");
    const countryCode = this.apiProxyService.getCountryCode();
    const options = {
      headers: Object.assign(
        {},
        headers,
        { "Referrer-Policy": "no-referrer" },
        { Referrer: "" },
        { "Country-Code": countryCode }
      ),
    };

    if (token) {
      options.headers["Authorization"] = "Bearer " + token;
    }
    if (this.openRequests === 0 && !silent) {
      setTimeout(() => {
        if (!this.spinnerRunning) {
          if (this.openRequests !== 0) {
            this.spinner.show();
            this.spinnerRunning = true;
          }
        }
      }, 200);
    }
    if (!silent) {
      this.openRequests++;
    }
    var path = this.urlBase + "/" + endpoint;
    if (urlOverride) {
      path = url;
    }

    if (method === HttpMethod.GET) {
      return this.http.get<any>(path, options).pipe(
        tap(() => this.hideSpinner(silent)),
        catchError((e) => this.handleError(e, silent))
      );
    }

    if (method === HttpMethod.DELETE) {
      return this.http.delete<any>(path, options).pipe(
        tap(() => this.hideSpinner(silent)),
        catchError((e) => this.handleError(e, silent))
      );
    }

    if (method === HttpMethod.POST) {
      return this.http.post<any>(path, data, options).pipe(
        tap(() => this.hideSpinner(silent)),
        catchError((e) => this.handleError(e, silent))
      );
    }

    if (method === HttpMethod.PUT) {
      return this.http.put<any>(path, data, options).pipe(
        tap(() => this.hideSpinner(silent)),
        catchError((e) => this.handleError(e, silent))
      );
    }
    this.hideSpinner(silent);
    return null;
  }

  private hideSpinner(silent: boolean): void {
    if (silent) {
      return;
    }
    this.openRequests--;
    if (this.openRequests < 0) {
      throw new Error("API Impossible negative requests happend");
    }
    if (this.spinnerRunning && this.openRequests === 0) {
      this.spinnerRunning = false;
      this.spinner.hide();
    }
  }

  // TODO: add all the other actions

  private handleError(error: Response, silent = false): Observable<never> {
    this.hideSpinner(silent);

    if (silent) {
      return null;
    }
    if (error.status === 404) {
      if (error.url.search("GetItemImageWeb") != null) {
        return throwError(new NotFoundError(error));
      }
      this.toastr.error(
        this.trans.instant("webPos.Errors.NotFound"),
        this.trans.instant("webPos.Errors.ApiError")
      );
      return throwError(new NotFoundError(error));
    }

    if (error.status === 401) {
      if (localStorage.getItem("authToken")) {
        //only throw 401 when token exists, ignore in the other cases
        this.toastr.error(
          this.trans.instant("webPos.Errors.Unauthorized"),
          this.trans.instant("webPos.Errors.ApiError")
        );
        return throwError(new UnAuthorizedError(error));
      }
      return null;
    }

    if (error.status === 400) {
      this.toastr.error(
        this.trans.instant("webPos.Errors.BadRequest"),
        this.trans.instant("webPos.Errors.ApiError")
      );
      return throwError(new BadRequestError(error["error"]));
    }
    this.toastr.error(
      this.trans.instant("webPos.Errors.ServerError"),
      this.trans.instant("webPos.Errors.ApiError")
    );
    return throwError(new UnknownError(error["error"]));
  }
}
